import { baseApiAxios } from './api';
import { displaySuccessPopup, showErrorFromApiOperation } from './main';
import { addButtonSpinner, removeSpinners } from './sidebar';
import { FormField } from './modalUtils';
import { col, initDataTable } from './datatable';
import { Datatable } from '../../Datatable';
import { Modal } from 'bootstrap';

// CLOUD-37764: list of the M2M client mappings of an AWS account (Account Details > M2M Client Mappings tab).
// Not related to the order-based M2M technical-user mapping in manageM2MApiAccess.js.

function getTableColumns(onDelete) {
  return [
    col('client_id', 'client_id_col', 'Client ID'),
    col('description', 'description_col', 'Description'),
    col('created_date', 'created_date_col', 'Created Date'),
    // Server-set (request.user_name). null for mappings created before the field existed -> shown as '-'.
    col('created_by', 'created_by_col', 'Created By'),
    {
      visible: true,
      defaultContent: '',
      orderable: false,
      searchable: false,
      data: null,
      name: 'actions_col',
      title: 'Actions',
      width: '50px',
      createdCell: function (td, _cellData, rowData) {
        const deleteButton = $(
          '<button class="btn btn-custom btn-xs delete-m2m-client-mapping" data-bs-toggle="tooltip" title="Delete M2M client mapping"/>',
        ).append('<i class="fas fa-trash-alt">');
        if (onDelete) {
          deleteButton.on('click', evt => onDelete(rowData, evt));
        } else {
          deleteButton.prop('disabled', true);
        }
        $('<div class="table-action-button-group">').append(deleteButton).appendTo(td);
      },
    },
  ];
}

/**
 * Latest load started per container. A response is only rendered if it belongs to the latest load, so a slow
 * earlier request (e.g. Refresh clicked twice) can never overwrite the result of a newer one.
 *
 * @type {WeakMap<HTMLElement, number>}
 */
const latestLoadByContainer = new WeakMap();

/**
 * Loads the M2M client mappings of the given account into the container: loading indicator, then either the
 * DataTable, the empty message or (on failure) the error popup plus an inline error.
 *
 * @param {HTMLElement} container the tab container to render into
 * @param {string} accountId the currently selected account
 * @param {{ onDelete?: (rowData: object, event: Event) => void }} [options]
 * @returns {Promise<boolean>} true if the mappings were loaded (or the load was superseded by a newer one),
 * false on failure
 */
export async function loadAccountM2MClientMappings(container, accountId, { onDelete } = {}) {
  const loadId = (latestLoadByContainer.get(container) || 0) + 1;
  latestLoadByContainer.set(container, loadId);
  const isSuperseded = () => latestLoadByContainer.get(container) !== loadId;

  $(container).html(
    <div class="text-center py-4">
      <i class="fas fa-spinner fa-spin me-2"></i> Loading M2M client mappings...
    </div>,
  );

  let items;
  try {
    // GET /accounts/{id}/client-mappings returns {items} in one response (no paging)
    const result = await baseApiAxios.getAccountM2MClientMappings(accountId);
    items = result?.items || [];
  } catch (err) {
    if (isSuperseded()) return true; // a newer load owns the container and the error reporting
    showErrorFromApiOperation('Error loading M2M client mappings')(err);
    $(container).html(
      <div class="alert alert-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <span>M2M client mappings could not be loaded.</span>
      </div>,
    );
    return false;
  }

  if (isSuperseded()) return true;

  if (items.length === 0) {
    $(container).html(
      <div class="alert alert-secondary">
        <i class="fas fa-info-circle me-2"></i>
        <span>No M2M clients are mapped to this account yet.</span>
      </div>,
    );
    return true;
  }

  const tableId = 'm2m-client-mappings-table-' + accountId;
  $(container).html(<Datatable tableId={tableId} />);
  initDataTable(tableId, [], getTableColumns(onDelete), undefined, {
    data: items,
    order: [[0, 'asc']],
  });
  return true;
}

// ── Add M2M client mappings (CLOUD-37764) ───────────────────────────────────────

// M2M client ids are UUIDs; same pattern as the order-based M2M access form and the new portal frontend.
export const CLIENT_ID_UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Validates the rows entered in the "Add M2M Client Mappings" form and builds the POST body.
 *
 * - Fully blank rows are ignored; at least one row is required.
 * - Client ID is required, must not contain whitespace, must be a UUID and must be unique (case-insensitive).
 * - An empty description is omitted from the payload. No other rules are applied (the backend schema has none).
 *
 * @param {{ client_id?: string, description?: string }[]} rows raw, untrimmed form values
 * @returns {{ payload: import('./api').AccountM2MClientMappingInput[]|null, error: {message: string, rowIndex: number}|null }}
 */
export function validateMappingRows(rows) {
  const entered = rows
    .map((row, rowIndex) => ({
      rowIndex,
      clientId: row.client_id ?? '',
      description: (row.description ?? '').trim(),
    }))
    .filter(row => row.clientId.trim() || row.description);

  if (entered.length === 0) {
    return { payload: null, error: { message: 'At least one Client ID is required.', rowIndex: 0 } };
  }

  const seen = new Set();
  for (const { rowIndex, clientId } of entered) {
    let message = null;
    if (!clientId.trim()) {
      message = 'Client ID is required for every entered row.';
    } else if (/\s/.test(clientId)) {
      message = 'Client ID cannot contain whitespace.';
    } else if (!CLIENT_ID_UUID_PATTERN.test(clientId)) {
      message = 'Client ID must be a valid UUID (e.g. 123e4567-e89b-12d3-a456-426614174000).';
    } else if (seen.has(clientId.toLowerCase())) {
      message = `Duplicate Client ID "${clientId}". Each Client ID can only be submitted once.`;
    }
    if (message) return { payload: null, error: { message, rowIndex } };
    seen.add(clientId.toLowerCase());
  }

  return {
    payload: entered.map(({ clientId, description }) =>
      description ? { client_id: clientId, description } : { client_id: clientId },
    ),
    error: null,
  };
}

const ADD_MAPPINGS_SUBMIT_BUTTON_ID = 'submit-add-m2m-client-mappings-button';

// Plain builder (not a JSX component), so there are no props to validate in the custom JSX runtime.
function createMappingRow(rowId) {
  return (
    <div class="m2m-mapping-row border rounded p-2 mb-2">
      <FormField
        id={'m2m-mapping-client-id-' + rowId}
        name="client_id"
        label="Client ID"
        placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
        title="m2m-client-id"
        pattern={CLIENT_ID_UUID_PATTERN.source}
        autocomplete="off"
      />
      <FormField
        id={'m2m-mapping-description-' + rowId}
        name="description"
        label="Description"
        placeholder="(optional) What this M2M client is used for."
        title="m2m-client-description"
      />
      <div class="text-end">
        <button type="button" class="btn btn-custom btn-xs remove-m2m-mapping-row" title="Remove this client ID">
          <i class="fas fa-trash-alt"></i> Remove
        </button>
      </div>
    </div>
  );
}

/**
 * Opens the shared modal to map one or more M2M client ids (each with an optional description) to an account.
 * Each row is submitted as its own POST (the backend accepts one mapping per request). On success the modal closes
 * and `onSuccess` is called; on failure the modal stays open with the failed rows and the standard API error
 * notification is shown.
 *
 * @param {string} accountId the currently selected account
 * @param {{ onSuccess?: () => void }} [options]
 */
export function openAddM2MClientMappingsModal(accountId, { onSuccess } = {}) {
  const modal = $('#sharedModal');
  $('#sharedModalTitle').html(<strong>Add M2M Client Mappings</strong>);

  let nextRowId = 0;
  let submitting = false;

  $('#sharedModalFooter').append(
    <button type="submit" class="btn btn-success" id={ADD_MAPPINGS_SUBMIT_BUTTON_ID}>
      Submit
    </button>,
  );
  $('#sharedModalContent').html(
    <form id="add-m2m-client-mappings-form" name="add-m2m-client-mappings-form" novalidate>
      <p>Map one or more M2M client IDs to this AWS account. The description is optional.</p>
      <div id="m2m-mapping-rows"></div>
      <div id="m2m-mapping-error" class="alert alert-danger d-none" role="alert"></div>
      <button type="button" class="btn btn-custom btn-xs" id="add-m2m-mapping-row">
        <i class="fas fa-plus"></i> Add another Client ID
      </button>
    </form>,
  );

  const rowsContainer = () => $('#m2m-mapping-rows');
  const clearError = () => {
    $('#m2m-mapping-error').addClass('d-none').text('');
    $('#sharedModalContent input').removeClass('is-invalid');
  };
  const syncRemoveButtons = () => {
    // Always keep at least one row.
    const onlyOne = rowsContainer().children().length <= 1;
    rowsContainer().find('.remove-m2m-mapping-row').prop('disabled', onlyOne);
  };
  const addRow = () => {
    const row = $(createMappingRow(nextRowId++));
    row.find('.remove-m2m-mapping-row').on('click', () => {
      if (submitting || rowsContainer().children().length <= 1) return;
      row.remove();
      syncRemoveButtons();
    });
    rowsContainer().append(row);
    syncRemoveButtons();
  };
  const resetRows = () => {
    rowsContainer().empty();
    clearError();
    addRow();
  };
  const readRows = () =>
    rowsContainer()
      .children()
      .toArray()
      .map(row => ({
        client_id: $(row).find('input[name="client_id"]').val(),
        description: $(row).find('input[name="description"]').val(),
      }));

  // One line per failed client id (with the backend's message), and the matching rows marked as invalid.
  const showFailedRows = failedRows => {
    const list = $('<ul class="mb-0">');
    failedRows.forEach(({ clientId, reason }) => {
      const message = reason?.response?.data?.message || reason?.message || 'Request failed';
      list.append($('<li>').text(`${clientId}: ${message}`));
      rowsContainer()
        .children()
        .toArray()
        .filter(
          row => String($(row).find('input[name="client_id"]').val()).trim().toLowerCase() === clientId.toLowerCase(),
        )
        .forEach(row => $(row).find('input[name="client_id"]').addClass('is-invalid'));
    });
    $('#m2m-mapping-error')
      .removeClass('d-none')
      .empty()
      .append($('<p class="mb-1">').text(`${failedRows.length} M2M client mapping(s) could not be added:`), list);
  };

  $('#add-m2m-client-mappings-form').on('submit', e => e.preventDefault());
  $('#add-m2m-mapping-row').on('click', () => {
    if (!submitting) addRow();
  });
  addRow();

  const submit = async () => {
    if (submitting) return; // prevent duplicate submission while a request is in flight

    clearError();
    const { payload, error } = validateMappingRows(readRows());
    if (error) {
      rowsContainer().children().eq(error.rowIndex).find('input[name="client_id"]').addClass('is-invalid');
      $('#m2m-mapping-error').removeClass('d-none').text(error.message);
      return;
    }

    submitting = true;
    const submitButton = document.getElementById(ADD_MAPPINGS_SUBMIT_BUTTON_ID);
    if (submitButton) submitButton.disabled = true;
    addButtonSpinner(ADD_MAPPINGS_SUBMIT_BUTTON_ID);
    try {
      // The backend accepts ONE {client_id, description?} object per POST, so every row is its own request.
      const results = await Promise.allSettled(
        payload.map(mapping => baseApiAxios.createAccountM2MClientMapping(accountId, mapping)),
      );
      const failures = results.filter(r => r.status === 'rejected');
      const createdCount = results.length - failures.length;

      if (failures.length === 0) {
        resetRows();
        Modal.getInstance(modal).hide();
        displaySuccessPopup(`${createdCount} M2M client mapping(s) created`, 'M2M Client Mappings Added');
        if (onSuccess) onSuccess();
        return;
      }

      // Keep only the rows that failed (with their values) so the user can correct and retry.
      const succeededIds = new Set(
        payload.filter((_, i) => results[i].status === 'fulfilled').map(m => m.client_id.toLowerCase()),
      );
      rowsContainer()
        .children()
        .toArray()
        .forEach(row => {
          const id = String($(row).find('input[name="client_id"]').val()).trim().toLowerCase();
          if (succeededIds.has(id)) $(row).remove();
        });
      if (rowsContainer().children().length === 0) addRow();
      syncRemoveButtons();
      showFailedRows(
        payload
          .map((mapping, i) => ({ mapping, result: results[i] }))
          .filter(({ result }) => result.status === 'rejected')
          .map(({ mapping, result }) => ({ clientId: mapping.client_id, reason: result.reason })),
      );
      showErrorFromApiOperation('Error adding M2M client mappings')(failures[0].reason);
      if (createdCount > 0 && onSuccess) onSuccess();
    } catch (err) {
      showErrorFromApiOperation('Error adding M2M client mappings')(err);
    } finally {
      submitting = false;
      if (submitButton) submitButton.disabled = false;
      removeSpinners();
    }
  };
  $('#' + ADD_MAPPINGS_SUBMIT_BUTTON_ID).on('click', submit);

  Modal.getInstance(modal).show();
}

// ── Delete M2M client mapping (CLOUD-37764) ─────────────────────────────────────

const DELETE_MAPPING_SUBMIT_BUTTON_ID = 'submit-delete-m2m-client-mapping-button';

// "<account_id>/<client_id>" of every DELETE currently in flight. Prevents a second request for the same mapping
// even if the confirmation is re-opened (e.g. the modal was dismissed while the first request was still running).
const deletesInFlight = new Set();

/**
 * Opens the shared confirmation modal (same layout as the "Revoke M2M Client Access" confirmation) for removing
 * one M2M client mapping from an account. Opening it does NOT call the API; only the confirm button sends
 * DELETE /accounts/{account_id}/client-mappings/{client_id}, exactly once.
 *
 * Dismissing the modal (Close / X) cancels without any request. On success the modal closes and `onSuccess` is
 * called so the caller can refresh the list. On failure the modal closes, the list is left untouched (the mapping
 * stays visible) and the standard API error notification is shown.
 *
 * @param {string} accountId the currently selected account
 * @param {{ client_id: string, description?: string|null }} mapping the selected row
 * @param {{ onSuccess?: () => void }} [options]
 */
export function openDeleteM2MClientMappingModal(accountId, mapping, { onSuccess } = {}) {
  const clientId = mapping.client_id;
  const requestKey = `${accountId}/${clientId}`;
  if (deletesInFlight.has(requestKey)) return;

  const modal = $('#sharedModal');
  $('#sharedModalTitle').html(<strong>Delete M2M Client Mapping</strong>);

  // A fast double click on the row's delete button must not stack up confirm buttons.
  $('#' + DELETE_MAPPING_SUBMIT_BUTTON_ID).remove();
  $('#sharedModalFooter').append(
    <button type="submit" class="btn btn-danger" id={DELETE_MAPPING_SUBMIT_BUTTON_ID}>
      Delete Mapping
    </button>,
  );
  $('#sharedModalContent').html(
    <>
      <h3 align="center">Are you sure ?</h3>
      <div class="alert alert-warning px-3 py-2 mb-3 bold-text">
        This M2M client will no longer be mapped to AWS account {accountId}. To map it again, add the mapping anew.
      </div>
      <form id="delete-m2m-client-mapping-form" name="delete-m2m-client-mapping-form" method="POST">
        <FormField id="delete-m2m-mapping-client-id" label="Client ID" disabled value={clientId} />
        {mapping.description && (
          <FormField id="delete-m2m-mapping-description" label="Description" disabled value={mapping.description} />
        )}
      </form>
    </>,
  );
  $('#delete-m2m-client-mapping-form').on('submit', e => e.preventDefault());

  const confirm = async () => {
    if (deletesInFlight.has(requestKey)) return; // prevent duplicate delete requests

    deletesInFlight.add(requestKey);
    const confirmButton = document.getElementById(DELETE_MAPPING_SUBMIT_BUTTON_ID);
    if (confirmButton) confirmButton.disabled = true;
    addButtonSpinner(DELETE_MAPPING_SUBMIT_BUTTON_ID);
    try {
      await baseApiAxios.deleteAccountM2MClientMapping(accountId, clientId);
      Modal.getInstance(modal).hide();
      displaySuccessPopup(`M2M client mapping for client_id=${clientId} removed`, 'M2M Client Mapping Deleted');
      if (onSuccess) onSuccess();
    } catch (err) {
      // Same behaviour as the other legacy confirmation modals: close it and report the error. The list is not
      // refreshed, so the mapping stays visible and can be deleted again.
      Modal.getInstance(modal).hide();
      showErrorFromApiOperation('Error deleting M2M client mapping')(err);
    } finally {
      deletesInFlight.delete(requestKey);
      if (confirmButton) confirmButton.disabled = false;
      removeSpinners();
    }
  };
  $('#' + DELETE_MAPPING_SUBMIT_BUTTON_ID).on('click', confirm);

  Modal.getInstance(modal).show();
}
